/**
 * rdn60 renal-stone overlay demo — cherry-picked allowlist.
 *
 * For these RadNet rdn60 studies we hand-segment the stone in the labeling
 * worklist `rdn60-renal-stone` (project_type: segmentation, lesion_type:
 * renal_stone). When one of these studies opens in the viewer, the overlay
 * loader fetches that saved mask + its `long_axis_mm` from the labeling store
 * and renders it across the MPR panes, and the report surfaces the longest
 * dimension. See #654, which shipped it (the spec doc it cited was never committed).
 *
 * Temporary / demo-only: the set is explicit so no labeling fetch fires on any
 * other study. The `projectId` is the labeling worklist id the masks are saved
 * under (annotations are keyed `{projectId}#{study_uid}` in DynamoDB).
 */

/** The labeling worklist (== DynamoDB project_id) the rdn60 stone masks live in. */
export const RDN60_STONE_PROJECT_ID = "rdn60-renal-stone";

/**
 * Bare `study_instance_uid`s of the 11 cherry-picked rdn60 stone cases.
 * (9 renal stone, 1 renal+ureteral `…59527536`, 1 ureteral-only `…18162063`.)
 */
export const RDN60_STONE_STUDY_UIDS: readonly string[] = [
  "2.25.102476584214362469562661197485776349608",
  "2.25.127394132583990963742378246476267780819",
  "2.25.14636589312585148264318141422347967335",
  "2.25.162461789734784596743838579340947221838",
  "2.25.254229048204583125344833129133772089400",
  "2.25.258252786400052079529610378152478978515",
  "2.25.282666420480358079936714406975018162063",
  "2.25.284397040226282337097262418877159527536",
  "2.25.285691883422303621277386160794519545615",
  "2.25.305137030624064088874027553601225466130",
  "2.25.51834339405131557067127420733125215924",
] as const;

const STONE_UID_SET = new Set(RDN60_STONE_STUDY_UIDS);

/**
 * If `studyId` is one of the cherry-picked rdn60 stone studies, return the
 * labeling project the saved mask lives in; otherwise `null` (→ no overlay,
 * no labeling fetch). Accepts either a bare uid or the composite
 * `"<uid>::reg-rdn60-<uid>"` studyId used by the viewer.
 */
export function getStoneOverlayProjectId(studyId: string | null | undefined): string | null {
  if (!studyId) return null;
  const bareUid = studyId.split("::")[0];
  return STONE_UID_SET.has(bareUid) ? RDN60_STONE_PROJECT_ID : null;
}
